export type CompanyResearchIntentType = "learn_more" | "sales" | "partnership" | "risk_check";

const COMPANY_RESEARCH_REPORT_INTENT_LABELS: Record<CompanyResearchIntentType, string> = {
  learn_more: "詳しく調べる",
  sales: "営業分析",
  partnership: "協業分析",
  risk_check: "リスク分析",
};

export function normalizeCompanyResearchIntentType(intent: unknown): CompanyResearchIntentType | null {
  return typeof intent === "string" && intent in COMPANY_RESEARCH_REPORT_INTENT_LABELS
    ? intent as CompanyResearchIntentType
    : null;
}

export function getCompanyResearchReportIntentLabel(intent: unknown): string {
  const normalized = normalizeCompanyResearchIntentType(intent);
  return normalized ? COMPANY_RESEARCH_REPORT_INTENT_LABELS[normalized] : "企業情報の確認";
}

export const COMPANY_RESEARCH_KNOWLEDGE_AUTO_REGISTRATION_NOTE =
  "企業情報の確認結果は調査履歴に保存されます。ナレッジライブラリへの自動追加は、「詳しく調べる」や目的別分析の完了結果が対象です（自動追加が有効な場合）。";

export type CompanyResearchUsageEstimate = {
  route_type: string;
  serpapi_calls: number;
  mini_calls: number;
  response_api_calls: number;
  estimated_cost_usd: number;
};

export type CompanyResearchCandidate = {
  candidate_id: string;
  company_name: string;
  company_url?: string | null;
  display_name?: string;
  location?: string;
  representative_name?: string;
  business_summary?: string;
  candidate_reason?: string;
  confidence?: "high" | "medium" | "low";
  needs_user_confirmation?: boolean;
  source_url?: string;
  source_type?: string;
  corporate_number?: string;
};

export type CompanyResearchCandidateSearchInput = {
  company_name: string;
  location?: string;
  representative_name?: string;
};

export type CompanyResearchCandidateSearchResponse = {
  status: "candidates_found" | "unresolved" | "failed" | "search_failed";
  search_id?: string;
  resolution_mode?: "single_high_confidence" | "user_selection" | "needs_hint" | "needs_more_info" | "search_failed";
  recommended_action?: "quick_summary" | "select_candidate" | "ask_user_hint" | "retry_later";
  query?: string;
  candidates?: CompanyResearchCandidate[];
  usage_estimate?: CompanyResearchUsageEstimate;
  message?: string;
  retryable?: boolean;
  warnings?: string[];
};

export type CompanyResearchQuickSummaryInput = {
  search_id?: string;
  candidate_id?: string;
  confirmation_quick_summary_id?: string;
  confirmed_candidate_key?: "original" | "rescued";
  company_name: string;
  company_url?: string | null;
  source_url?: string | null;
  location?: string;
  representative_name?: string;
  business_summary?: string;
  industry?: string;
};

export type CompanyResearchIdentityCandidate = {
  name: string;
  display_name?: string;
  url?: string | null;
  domain?: string | null;
  location?: string | null;
  representative?: string | null;
};

export type CompanyResearchIdentityConfirmation = {
  reason_code: "identity_mismatch";
  message: string;
  original_candidate: CompanyResearchIdentityCandidate;
  rescued_candidate: CompanyResearchIdentityCandidate;
};

export type CompanyResearchAvailableAction = {
  action_type: CompanyResearchIntentType;
  label: string;
  description?: string;
  enabled?: boolean;
  status?: "available" | "coming_soon" | "disabled";
};

export type CompanyResearchSource = {
  title?: string;
  url?: string;
  source_type?: string;
  officialness?: "official" | "third_party" | "unknown";
  freshness_note?: string;
};

export type CompanyResearchQuickSummaryStructuredResult = {
  company?: {
    name?: string;
    company_name?: string;
    official_website?: string;
    official_url?: string;
    location?: string;
    representative_name?: string;
    founded?: string;
    business_summary?: string;
    main_services?: string[];
    resolution_status?: string;
  };
  quick_sources?: CompanyResearchSource[];
  confidence?: string;
  available_actions?: CompanyResearchAvailableAction[];
};

export type CompanyResearchQuickSummaryResponse = {
  status: "completed" | "needs_confirmation" | "needs_more_info" | "failed";
  quick_summary_id?: string;
  summary_level: "quick";
  display_text?: string;
  structured_result?: CompanyResearchQuickSummaryStructuredResult;
  candidate?: CompanyResearchCandidate;
  confirmation?: CompanyResearchIdentityConfirmation;
  requested_hints?: string[];
  message?: string;
  usage_estimate?: CompanyResearchUsageEstimate;
  warnings?: string[];
};

export type CompanyResearchCreateJobInput = {
  quick_summary_id?: string;
  intent_type: CompanyResearchIntentType;
  company_name?: string;
  company_url?: string | null;
  quick_summary?: CompanyResearchQuickSummaryResponse | Record<string, unknown>;
  base_job_id?: string | null;
  self_profile_id?: string | null;
  self_company_url?: string | null;
  self_company_knowledge_item_id?: string | null;
  email_delivery?: boolean;
  email_to?: string;
  send_email?: boolean;
  email?: string;
  requested_by?: string;
  client_request_id?: string;
};

export type CompanyResearchJobAccepted = {
  status: "accepted";
  job_id: string;
  message?: string;
  stage?: string;
  retryable?: false;
};

export type CompanyResearchJobStatus =
  | "accepted"
  | "queued"
  | "pending"
  | "running"
  | "processing"
  | "completed"
  | "failed"
  | "needs_more_info"
  | "ambiguous";

export type CompanyResearchSelfCompanyKnowledgeReference = {
  title: string;
  type: string;
  source_type: string | null;
};

export type CompanyResearchJobError = {
  code?: string;
  user_message?: string;
  developer_message?: string;
  message?: string;
};

export type CompanyResearchJobDetail = {
  job_id: string;
  knowledge_item_id?: string | null;
  status: CompanyResearchJobStatus;
  stage?: string;
  result_type?: "success" | "partial_success" | "failure";
  intent_type?: CompanyResearchIntentType;
  company_name?: string;
  company_url?: string | null;
  quick_summary_id?: string;
  display_text?: string;
  report_text?: string;
  report_structured?: Record<string, unknown>;
  structured_result?: Record<string, unknown>;
  short_summary?: string;
  message?: string;
  error?: string | CompanyResearchJobError | null;
  retryable?: boolean;
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
  delivery?: {
    requested?: boolean;
    status?: string;
    recipient?: string | null;
  };
  self_company_knowledge?: CompanyResearchSelfCompanyKnowledgeReference | null;
  warnings?: string[];
  usage_estimate?: CompanyResearchUsageEstimate;
  usage_json?: CompanyResearchUsageEstimate;
  detailed_research_job_id?: string | null;
  detailed_research_used?: boolean;
};

export type CompanyResearchJobListItem = {
  job_id: string;
  status: CompanyResearchJobStatus;
  intent_type?: CompanyResearchIntentType;
  company_name?: string;
  company_url?: string | null;
  short_summary?: string;
  error?: string | CompanyResearchJobError | null;
  self_company_knowledge?: CompanyResearchSelfCompanyKnowledgeReference | null;
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
};

export type CompanyResearchJobListInput = {
  status?: CompanyResearchJobStatus;
  limit?: number;
  cursor?: string;
};

export type CompanyResearchJobList = {
  jobs?: CompanyResearchJobListItem[];
  items?: CompanyResearchJobListItem[];
  next_cursor?: string | null;
};

export type CompanyResearchRecordStatus = "completed" | "needs_more_info" | "failed" | "running";

export type CompanyResearchRecordListItem = {
  quick_summary_id: string;
  search_id?: string;
  summary_level?: "quick" | string;
  company_name?: string;
  company_url?: string | null;
  status: CompanyResearchRecordStatus;
  display_text?: string;
  created_at?: string;
  updated_at?: string;
};

export type CompanyResearchRecordListInput = {
  cursor?: string;
};

export type CompanyResearchRecordList = {
  records?: CompanyResearchRecordListItem[];
  items?: CompanyResearchRecordListItem[];
  next_cursor?: string | null;
};

export type CompanyResearchRecordDetail = {
  quick_summary_id: string;
  search_id?: string;
  summary_level?: "quick" | string;
  company_name?: string;
  company_url?: string | null;
  status: CompanyResearchRecordStatus;
  display_text?: string;
  structured_result?: CompanyResearchQuickSummaryStructuredResult | Record<string, unknown>;
  usage_estimate?: CompanyResearchUsageEstimate;
  deep_research_jobs?: CompanyResearchJobDetail[];
  created_at?: string;
  updated_at?: string;
};

export type CompanyResearchClient = {
  searchCandidates(input: CompanyResearchCandidateSearchInput): Promise<CompanyResearchCandidateSearchResponse>;
  createQuickSummary(input: CompanyResearchQuickSummaryInput): Promise<CompanyResearchQuickSummaryResponse>;
  createJob(input: CompanyResearchCreateJobInput): Promise<CompanyResearchJobAccepted>;
  getJob(jobId: string): Promise<CompanyResearchJobDetail>;
  listJobs(input?: CompanyResearchJobListInput): Promise<CompanyResearchJobList>;
  listRecords(input?: CompanyResearchRecordListInput): Promise<CompanyResearchRecordList>;
  getRecord(quickSummaryId: string): Promise<CompanyResearchRecordDetail>;
};
